import { useCallback, useEffect, useState } from "react";
import { Title, usePermissions } from "react-admin";
import { Alert, Box, CircularProgress, Typography, useMediaQuery, useTheme } from "@mui/material";
import DateRangeFilter from "@/components/DateRangeFilter";
import { callApi } from "@/dataprovider/misc-apis";
import AccountsMetrics from "./components/AccountsMetrics";
import AccountsChartsRow from "./components/AccountsChartsRow";
import RecentReceiptsTable from "./components/RecentReceiptsTable";
import AccountsQuickNav from "./components/AccountsQuickNav";
import {
  filterAccountsRecentReceipts,
  resolveAccountsStreamAccess,
} from "./accounts-dashboard-access";
import type { AccountsDashboardStats } from "./types";

const getDefaultDateRange = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const fyStartYear = currentMonth >= 4 ? currentYear : currentYear - 1;
  return {
    startDate: `${fyStartYear}-04-01`,
    endDate: `${fyStartYear + 1}-03-31`,
  };
};

export default function AccountsDashboard() {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const { permissions } = usePermissions();
  const defaultDateRange = getDefaultDateRange();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AccountsDashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  const streamAccess = resolveAccountsStreamAccess(permissions);
  const showSabil = streamAccess.sabil;
  const showLagat = streamAccess.lagat;
  const showBooking = streamAccess.booking;
  const showNiyaaz = streamAccess.niyaaz;
  const showYearlyNiyaaz = streamAccess.yearlyNiyaaz;

  const fetchStats = useCallback(async (startDate: string, endDate: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await callApi({
        location: "accounts",
        method: "GET",
        id: "dashboard/stats",
        data: { startDate, endDate },
      });
      setStats((response?.data as AccountsDashboardStats) ?? null);
    } catch (err) {
      console.error("Error fetching accounts dashboard stats:", err);
      setError(err instanceof Error ? err.message : "Failed to load dashboard data");
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(defaultDateRange.startDate, defaultDateRange.endDate);
  }, [defaultDateRange.endDate, defaultDateRange.startDate, fetchStats]);

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    fetchStats(startDate, endDate);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        px: { xs: 1, sm: 2 },
        pb: { xs: 2, sm: 3 },
      }}
    >
      <Title title="Accounts" />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 0.5,
          mb: { xs: 1.5, sm: 1 },
        }}
      >
        <Typography variant={isSmall ? "h6" : "h5"} component="h1" sx={{ fontWeight: 600 }}>
          Accounts overview
        </Typography>
        {stats?.period?.financialYear && (
          <Typography variant="body2" color="text.secondary">
            FY {stats.period.financialYear}
          </Typography>
        )}
      </Box>

      <DateRangeFilter
        defaultStartDate={defaultDateRange.startDate}
        defaultEndDate={defaultDateRange.endDate}
        onDateChange={handleDateRangeChange}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: { xs: 6, sm: 8 } }}>
          <CircularProgress size={isSmall ? 32 : 40} />
        </Box>
      ) : stats ? (
        <>
          <AccountsMetrics
            stats={stats}
            showSabil={showSabil}
            showLagat={showLagat}
            showBooking={showBooking}
            showNiyaaz={showNiyaaz}
            showYearlyNiyaaz={showYearlyNiyaaz}
          />
          <AccountsChartsRow
            stats={stats}
            showSabil={showSabil}
            showLagat={showLagat}
            showBooking={showBooking}
            showNiyaaz={showNiyaaz}
            showYearlyNiyaaz={showYearlyNiyaaz}
          />
          <RecentReceiptsTable
            receipts={filterAccountsRecentReceipts(stats.recentReceipts, streamAccess)}
          />
        </>
      ) : null}

      <AccountsQuickNav />
    </Box>
  );
}
